import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {secureGetPdf, secureGet } from '../../config/api';
import { formatDateTime, formatAmount } from '../../utils/format';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';
import LoadingLogo from '../../components/LoadingLogo';
import exportIcon from '../../icons/export.svg';

const BillingInvoiceDetails = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(() => location.state?.invoice ?? location.state?.row ?? {});

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await secureGet(`/billing-invoices/${id}`, showSnackbar, { signal });
      setInvoice(response.data || {});
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Failed to fetch billing invoice:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchInvoice();
    return () => abortController.abort();
  }, [id, abortController, fetchInvoice]);

  const getStatusStyle = (status) => {
    const statusUpper = (status || '').toUpperCase();
    if (statusUpper === 'PAID' || statusUpper === 'FINALIZED') return 'bg-[#06D6A0] text-white';
    if (statusUpper === 'DRAFT') return 'bg-[#A3A3A3] text-white';
    if (statusUpper === 'OVERDUE') return 'bg-[#EF476F] text-white';
    return 'bg-[#707070] text-white';
  };

  const renderInvoiceStatus = (status) => (
    <span className={`px-2 py-1 rounded-[10px] font-semibold text-[12px] ${getStatusStyle(status)}`}>
      {t(`billingInvoiceStatus.${status}`) || '—'}
    </span>
  );

  const handleDownloadClick = async () => {
    if (!invoice?.id) return;
    try {
      const response = await secureGetPdf(`/billing-invoices/${invoice.id}/pdf`, showSnackbar);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoice.invoiceNumber || invoice.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const renderActionButtons = () => (
    <>
      <button
        type="button"
        className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDownloadClick}
      >
        <img src={exportIcon} alt="" className="w-5 h-5" />
        {t('actions.downloadInvoice')}
      </button>
    </>
  );

  const itemTypeLabel = (itemType) =>
    itemType ? t(`fields.invoice.itemType_${itemType}`, { defaultValue: itemType }) : '';

  if (loading) {
    return <LoadingLogo />;
  }

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t('titles.billingInvoiceDetails')}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black mt-2">{t('titles.billingInvoiceDetails')}</h1>

        <div className="flex justify-between items-center flex-wrap">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center space-x-[10px] flex-wrap">
              <p className="text-black font-semibold text-[24px]">
                {invoice.invoiceNumber || `#${invoice.id}`}
              </p>
              {renderInvoiceStatus(invoice.status)}
            </div>
            {invoice.clientName && (
              <p className="text-[#38485C] text-[20px] font-semibold mt-1">{invoice.clientName}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3">{renderActionButtons()}</div>
        </div>
      </div>

      <div className="w-full flex flex-wrap justify-between bg-white py-4 pl-[32px] pr-4 rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4 gap-y-6">
        <div className="flex-1 min-w-[140px]">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.issuedAt')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(invoice.issuedAt)}</p>
        </div>
        <div className="flex-1 min-w-[140px]">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.dueDate')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(invoice.dueDate)}</p>
        </div>
        <div className="flex-1 min-w-[140px]">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.periodEnd')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">{formatDateTime(invoice.periodEnd)}</p>
        </div>
        <div className="flex-1 min-w-[140px]">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.totalHt')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(invoice.priceHT ?? invoice.priceHt)}
          </p>
        </div>
        <div className="flex-1 min-w-[140px]">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">{t('billingInvoices.totalTtc')}</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(invoice.priceTTC ?? invoice.priceTtc)}
          </p>
        </div>
      </div>

      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">{t('fields_types.list')}</h3>

      <div className="space-y-2">
        {(invoice.details || []).map((detail, index) => {
          const price = detail.price ?? detail.unitPrice ?? 0;
          const quantity = detail.quantity ?? 1;
          if (invoice.extraSeatsInvoice) {
            return (
              <div
                key={detail.id ?? index}
                className="grid grid-cols-[2fr_1fr_1fr_1fr] items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-4"
              >
                <div>
                  <p className="text-[#043F88] text-[16px] font-semibold">{itemTypeLabel(detail.itemType) || '—'}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('rows.quantity').toUpperCase()}</p>
                  <p className="text-[#043F88] text-[20px] font-medium">{quantity}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('fields.invoice.unitPrice')}</p>
                  <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(price)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#707070] text-[12px] font-semibold">{t('rows.totalPrice')}</p>
                  <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(quantity * price * 1.2)}</p>
                </div>
              </div>
            );
          }
          const priceTotal = price * 1.2;
          return (
            <div
              key={detail.id ?? index}
              className="grid grid-cols-[2fr_1fr_1fr] items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-4"
            >
              <div>
                <p className="text-[#043F88] text-[16px] font-semibold">
                  {detail.itemType
                    ? `${itemTypeLabel(detail.itemType)}: ${detail.planName ? t(`plans.${detail.planName}`) : ''}${
                        detail.planName && detail.includedSeats != null ? ' - ' : ''
                      }${detail.includedSeats != null ? `${detail.includedSeats} ${t('fields.invoice.seats')}` : ''}`
                    : ''}
                </p>
              </div>
              <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">{t('fields.invoice.unitPrice')}</p>
                <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(price)}</p>
              </div>
              <div className="text-center">
                <p className="text-[#707070] text-[12px] font-semibold">{t('rows.totalPrice')}</p>
                <p className="text-[#043F88] text-[20px] font-semibold">{formatAmount(priceTotal)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default BillingInvoiceDetails;
